import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { useLang } from "./LangContext";

/**
 * Lightweight global "dirty form" guard.
 *
 * Pages register an `{ isDirty, save, title }` snapshot via `useDirtyGuard`.
 * Any navigation/action wrapped in `guardedRun(action)` will:
 *   - run the action immediately if no page is dirty
 *   - otherwise pop a dialog "هل تريد حفظ هذه البيانات؟ نعم / لا"
 *       • نعم  → call save(); on success run action(); on failure stay
 *       • لا   → discard, run action()
 *
 * `save` may return false (or throw) to indicate validation failure; in that
 * case the navigation is cancelled and the user stays on the page.
 */
const DirtyContext = createContext(null);

export function useDirty() {
    const ctx = useContext(DirtyContext);
    if (!ctx) throw new Error("useDirty must be used inside <DirtyProvider>");
    return ctx;
}

/**
 * Page-level hook. Keeps the registered snapshot in sync with the latest
 * `isDirty` / `save` values, and unregisters on unmount.
 *
 * Usage:
 *   useDirtyGuard({ isDirty, save: async () => { ... return true|false; }, title });
 */
export function useDirtyGuard({ isDirty, save, title }) {
    const { _register, _unregister } = useDirty();
    // Keep a stable handle that always sees the latest values without re-registering.
    const ref = useRef({ isDirty, save, title });
    ref.current = { isDirty, save, title };

    useEffect(() => {
        _register(ref);
        return () => _unregister(ref);
    }, [_register, _unregister]);
}

export function DirtyProvider({ children }) {
    // active page snapshots (typically 0..1 — but we keep an array for safety)
    const refs = useRef(new Set());
    const [pending, setPending] = useState(null); // { action, title }
    const [busy, setBusy] = useState(false);
    const { t } = useLang();

    const _register = useCallback((r) => {
        refs.current.add(r);
    }, []);
    const _unregister = useCallback((r) => {
        refs.current.delete(r);
    }, []);

    // Find the (single) dirty snapshot, if any.
    const getDirty = () => {
        for (const r of refs.current) {
            if (r.current && r.current.isDirty) return r.current;
        }
        return null;
    };

    /**
     * Wrap a navigation/action. Returns void.
     * If something is dirty, opens the confirm dialog; otherwise runs immediately.
     */
    const guardedRun = useCallback((action) => {
        const dirty = getDirty();
        if (!dirty) {
            action();
            return;
        }
        setPending({
            action,
            title: dirty.title || t.saveDataTitle,
            save: dirty.save,
        });
    }, [t]);

    const onYes = async () => {
        if (!pending) return;
        setBusy(true);
        try {
            const ok = await pending.save?.();
            // save() may return false to cancel navigation (e.g. validation error)
            if (ok === false) {
                setBusy(false);
                setPending(null);
                return;
            }
            // After a successful save, refs are still attached but their isDirty
            // should have been cleared by the page itself (e.g. reset / snapshot update).
            const action = pending.action;
            setBusy(false);
            setPending(null);
            // Defer to next tick so React state updates flush before navigating.
            setTimeout(() => action?.(), 0);
        } catch {
            setBusy(false);
            setPending(null);
        }
    };

    const onNo = () => {
        if (!pending) return;
        const action = pending.action;
        // Force-clear all dirty flags so a subsequent guardedRun in the same tick
        // doesn't re-trigger the dialog. The page will be unmounted next anyway.
        for (const r of refs.current) {
            if (r.current) r.current.isDirty = false;
        }
        setPending(null);
        setTimeout(() => action?.(), 0);
    };

    return (
        <DirtyContext.Provider value={{ _register, _unregister, guardedRun }}>
            {children}
            <AlertDialog
                open={!!pending}
                onOpenChange={(v) => {
                    if (!v && !busy) setPending(null);
                }}
            >
                <AlertDialogContent dir={t.dir} data-testid="dirty-confirm-dialog">
                    <AlertDialogHeader className="text-start">
                        <AlertDialogTitle>{pending?.title || t.saveDataTitle}</AlertDialogTitle>
                        <AlertDialogDescription>
                            {t.unsavedHint}
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter className="flex-row-reverse gap-2 sm:flex-row-reverse">
                        {/* Cancel — stay on the page, keep editing. */}
                        <AlertDialogCancel
                            disabled={busy}
                            data-testid="dirty-confirm-cancel"
                        >
                            {t.dirtyCancel}
                        </AlertDialogCancel>
                        {/* Discard changes — abandon edits and run the queued action. */}
                        <button
                            type="button"
                            disabled={busy}
                            onClick={(e) => {
                                e.preventDefault();
                                onNo();
                            }}
                            className="inline-flex h-10 items-center justify-center rounded-md border border-red-200 bg-red-50 px-4 text-sm font-semibold text-red-700 hover:bg-red-100 disabled:opacity-50"
                            data-testid="dirty-confirm-discard"
                        >
                            {t.dirtyDiscard}
                        </button>
                        {/* Save — run save() then continue navigation. */}
                        <AlertDialogAction
                            disabled={busy}
                            onClick={(e) => {
                                e.preventDefault();
                                onYes();
                            }}
                            className="bg-neutral-900 text-white hover:bg-neutral-800"
                            data-testid="dirty-confirm-save"
                        >
                            {t.dirtySave}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </DirtyContext.Provider>
    );
}

/**
 * Browser-tab guard: warn if user tries to close/reload while dirty.
 * Re-export of the helper that lives in DirtyGuards.jsx for convenience.
 */
export function useUnsavedChangesWarning(isDirty) {
    const ref = useRef(isDirty);
    ref.current = isDirty;
    useEffect(() => {
        const handler = (e) => {
            if (ref.current) {
                e.preventDefault();
                e.returnValue = "";
                return "";
            }
            return undefined;
        };
        window.addEventListener("beforeunload", handler);
        return () => window.removeEventListener("beforeunload", handler);
    }, []);
}
